import { useEffect } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { playSound } from '../audio/sounds.js';
import { GameIcon } from './GameIcon.js';
import type { MultiGameEndedView } from '../../shared/protocol.js';

const MEDALS = ['🥇', '🥈', '🥉'];

/** Result of a multiplayer game: placements and points (decision 2026-09-27). */
export function MultiResultDialog(props: { result: MultiGameEndedView; userId: number; maintenance?: boolean; onClose: () => void }) {
  const { result } = props;
  const mine = result.standings.find((s) => s.player.id === props.userId);
  const solo = result.standings.length === 1;
  const won = Boolean(mine && !mine.dropped && mine.placement === 1 && result.reason !== 'aborted');
  // Valepaskahousu (user request 2026-09-30): only the one left with cards loses – the paskahousu,
  // who is announced to everybody with a tune. The places in between are not losses.
  const vale = result.gameType === 'vale' && result.reason === 'game_complete';
  const inGame = result.standings.filter((s) => !s.dropped);
  const paskahousu = vale && inGame.length > 1 ? inGame.reduce((a, b) => (b.placement > a.placement ? b : a)) : null;
  const iAmPaskahousu = Boolean(paskahousu && mine && paskahousu.player.id === mine.player.id);
  const outcome = result.reason === 'aborted' || !mine ? 'none' : won ? 'win' : vale && !iAmPaskahousu && !mine.dropped ? 'none' : 'loss';

  useEffect(() => {
    if (paskahousu) {
      playSound(iAmPaskahousu ? 'sadTrombone' : 'paskahousu');
      if (won) playSound('victory', 2600);
      return;
    }
    if (outcome === 'win') playSound('victory');
    else if (outcome === 'loss') playSound('defeat');
  }, [result.id]);

  const title = mine?.dropped
    ? t.multi.youLeft
    : solo
      ? t.multi.resultTitle
      : paskahousu && !won
        ? iAmPaskahousu
          ? t.result.title.loss
          : t.vale.survived
        : t.result.title[outcome];

  return (
    <div class="overlay" role="presentation">
      <div class={`dialog card result multi-result result-${outcome}`} role="alertdialog" aria-modal="true">
        {paskahousu && (
          <div class={`paskahousu-banner ${iAmPaskahousu ? 'me' : ''}`} role="status">
            <span class="paskahousu-pants" aria-hidden="true">
              👖
            </span>
            <span>{iAmPaskahousu ? t.vale.youArePaskahousu : t.vale.isPaskahousu(paskahousu.player.displayName)}</span>
          </div>
        )}
        <div class="result-title">
          <GameIcon game={result.gameType} size={34} /> {title}
        </div>
        {mine && !mine.dropped && !solo && result.reason !== 'aborted' && (
          <p class="result-score">{t.multi.yourPlacement(mine.placement, result.standings.length)}</p>
        )}
        <ol class="standings">
          {result.standings.map((s) => (
            <li class={`standing ${s.player.id === props.userId ? 'me' : ''} ${s.dropped ? 'dropped' : ''}`}>
              <span class="standing-place">
                {paskahousu?.player.id === s.player.id ? '👖' : !s.dropped && s.placement <= 3 && !solo ? MEDALS[s.placement - 1] : t.multi.placement(s.placement)}
              </span>
              <span class="standing-name">
                {s.player.isAi && '🤖 '}
                {s.player.displayName}
                {s.dropped && <span class="col-note"> · {t.multi.left}</span>}
              </span>
              {result.gameType !== 'vale' && <span class="standing-score">{t.multi.points(s.score)}</span>}
            </li>
          ))}
        </ol>
        <p class="muted">
          {result.reason === 'aborted' && props.maintenance ? t.closure.gameAborted : vale ? t.vale.endReason : t.multi.reason[result.reason]}
        </p>
        <div class="dialog-actions">
          <button class="btn btn-primary" onClick={props.onClose} autofocus>
            {t.result.backToArena}
          </button>
        </div>
      </div>
    </div>
  );
}
